import { useContext, useState } from "react";
import { buildClientFormData } from "../../../utils/helpers";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import CreateEditClientHeader from "./CreateEditClientHeader";
import CreateEditClientForm from "./CreateEditClientForm";
import BaseLoader from "../../../components/ui/BaseLoader";
import { LayoutContext } from "../../../context/LayoutContext";
import {
  createClient,
  getClientAccountById,
  getClientById,
  updateClient,
} from "../../../services/api/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";

const websiteTest = (value) => {
  if (!value) return false;
  const formatted = value.startsWith("http") ? value : `https://${value}`;
  try {
    const url = new URL(formatted);
    return /^(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z]{2,})+$/.test(url.hostname);
  } catch {
    return false;
  }
};

const commonFields = {
  clientName: Yup.string().required("Client Name is required"),
  branchId: Yup.number().required("Branch is required"),
  address: Yup.string().required("Address is required"),
  city: Yup.string().required("City is required"),
  state: Yup.string().notRequired(),
  zipCode: Yup.string().required("Zip Code is required"),
  country: Yup.string().required("Country is required"),
  phone: Yup.string().required("Phone is required"),
  email: Yup.string()
    .required("Email is required")
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "Invalid email format"),
  // Optional, but still has to look like a website when given.
  website: Yup.string()
    .notRequired()
    .test("is-valid-website", "Enter a valid website ", (value) => !value || websiteTest(value)),
  policies: Yup.string().required("Policies are required"),
};

export const parentValidationSchema = Yup.object({
  ...commonFields,
  branchId: Yup.number().notRequired(),
  manager: Yup.string().notRequired(),
  teamEmail: Yup.string().notRequired().email("Invalid email format"),
});

export const childValidationSchema = Yup.object({
  ...commonFields,
  teamId: Yup.string().required("Service Delivery Team is required"),
  secondaryTeamId: Yup.string()
    .required("Operations Team is required")
    .test(
      "different-from-team",
      "Secondary team must be different from the primary team",
      function (value) {
        if (!value) return true;
        return value !== this.parent.teamId;
      },
    ),
  manager: Yup.string().required("Contact Person is required"),
  registrationNumber: Yup.string().trim().required("Registration Number is required"),
  registrationCertificate: Yup.mixed().required("Registration Certificate is required"),
  accountsPerson: Yup.string().required("Accounts Person is required"),
  accountsEmail: Yup.string().required("Email is required").email("Invalid email format"),
  accountsPhone: Yup.string().notRequired(),
  paymentTerms: Yup.string().notRequired(),
  taxType: Yup.string().notRequired(),
  taxPercentage: Yup.number()
    .notRequired()
    .min(0, "Tax cannot be negative")
    .max(100, "Tax cannot exceed 100"),
  taxRegistrationNumber: Yup.string().notRequired(),
});

const CreateEditClient = ({ clientId, actionsContainer = null, viewOnly = false }) => {
  const { isBannerVisible } = useContext(LayoutContext);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id: routeId, parentClientId } = useParams();
  const id = clientId ?? routeId;
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const isParentClientCreate = searchParams.get("isParentClient") === "true";
  const isEdit = Boolean(id);
  const [isEditable, setIsEditable] = useState(false);

  const {
    isPending: isUpdateClientApiLoading,
    data: updatedClientData,
    mutateAsync: updateClientMutation,
  } = useMutation({
    mutationFn: updateClient,
    enabled: false,
  });

  const {
    isPending: isCreateClientApiLoading,
    data: createClientData,
    mutateAsync: createClientMutation,
  } = useMutation({
    mutationFn: createClient,
    enabled: false,
  });

  const { isPending: isfetchClientByIdApiLoading, data: clientData } = useQuery({
    queryKey: ["client", id],
    queryFn: () => getClientById(id),
    enabled: !!id,
  });
  const { data: accountData, isLoading: isFetching } = useQuery({
    queryKey: ["clientAccount", id],
    queryFn: () => getClientAccountById(id, true),
    enabled: !!id,
  });

  const selectedClient = clientData?.data;
  const isParentClient = isParentClientCreate || Boolean(selectedClient?.isParentClient);
  const isEntity = Boolean(parentClientId) || Boolean(selectedClient?.parentClientId);

  const formik = useFormik({
    initialValues: {
      clientName: selectedClient?.clientName || "",
      manager: selectedClient?.managerName || "",
      address: selectedClient?.address?.addressLine1 || "",
      city: selectedClient?.address?.city || "",
      state: selectedClient?.address?.state || "",
      zipCode: selectedClient?.address?.zipCode || "",
      country: selectedClient?.address?.country || "",
      phone: selectedClient?.phoneNumber || "",
      email: selectedClient?.email || "",
      teamEmail: selectedClient?.teamEmail || "",
      website: selectedClient?.website || "",
      policies: selectedClient?.policies || "",
      // instructions: selectedClient?.instructions || "",
      clientLogo: selectedClient?.clientLogo || null,
      registrationCertificate: selectedClient?.registrationCertificate || null,
      latitude: selectedClient?.address?.latitude || "",
      longitude: selectedClient?.address?.longitude || "",
      branchId: selectedClient?.branchId || null,
      teamId: selectedClient?.teamId || "",
      secondaryTeamId: selectedClient?.secondaryTeamId || "",

      accountsPerson: accountData?.data?.accountsPerson || "",
      accountsEmail: accountData?.data?.email || "",
      accountsPhone: accountData?.data?.phone || "",
      paymentTerms: accountData?.data?.paymentTerms || null,
      taxType: accountData?.data?.taxType || null,
      taxPercentage: accountData?.data?.taxPercentage || null,
      taxRegistrationNumber: selectedClient?.taxRegistrationNumber || "",
      registrationNumber: selectedClient?.registrationNumber || "",
    },
    enableReinitialize: true,
    validationSchema: isParentClient ? parentValidationSchema : childValidationSchema,
    onSubmit: (values) => {
      handleClientCreateOrUpdateButton(values);
    },
  });

  const handleClientCreateOrUpdateButton = async (values) => {
    try {
      const formData = buildClientFormData(values);

      if (!isEdit) {
        if (isParentClientCreate) {
          formData.append("isParentClient", "true");
        }
        if (parentClientId) {
          formData.append("parentClientId", parentClientId);
        }
      }

      isEdit ? await updateClientMutation({ id, formData }) : await createClientMutation(formData);
      dispatch(
        showToast({
          type: "success",
          message: isEdit ? "Customer updated successfully" : "Customer created successfully",
        }),
      );
      if (isEdit) {
        await queryClient.invalidateQueries({ queryKey: ["clientAccount", id] });
        await queryClient.invalidateQueries({ queryKey: ["client", id] });
      }
      setIsEditable(false);
      if (!clientId) {
        const effectiveParentId = selectedClient?.parentClientId || parentClientId;
        navigate(
          effectiveParentId
            ? `/workBoard/clients/${effectiveParentId}?tab=entities`
            : "/workBoard/clients",
        );
      }
    } catch (error) {
      dispatch(
        showToast({
          type: "error",
          // A network failure has no response body; reading it blindly threw and
          // swallowed the real error.
          message: error?.response?.data?.message || "Something went wrong. Please try again.",
        }),
      );
    }
  };

  if (isEdit && isfetchClientByIdApiLoading) {
    return <BaseLoader isLoading={isfetchClientByIdApiLoading} />;
  }

  return (
    <div
      className={`bg-[#F6F6F6] h-full relative ${
        clientId ? "" : isBannerVisible ? "top-[107px]" : "top-[56px]"
      }`}
    >
      <CreateEditClientHeader
        onSave={formik.handleSubmit}
        isLoading={isUpdateClientApiLoading || isCreateClientApiLoading}
        isDisabled={!formik.isValid || !formik.dirty}
        isEdit={isEdit}
        hideTitle={Boolean(clientId) && !viewOnly}
        actionsContainer={actionsContainer}
        viewOnly={viewOnly}
        isEditable={isEditable}
        setIsEditable={setIsEditable}
        onCancel={() => {
          formik.resetForm();
          setIsEditable(false);
        }}
        ownerId={selectedClient?.ownerId}
        selectedClient={selectedClient}
      />
      <CreateEditClientForm
        compactTop={Boolean(clientId)}
        formik={formik}
        isEditable={!viewOnly && isEditable}
        isEditMode={isEdit}
        isParentClient={isParentClient}
        recordLabel={isEntity ? "Entity" : undefined}
        linkedBranch={selectedClient?.branch}
        linkedTeam={selectedClient?.team}
        linkedSecondaryTeam={selectedClient?.secondaryTeam}
      />
    </div>
  );
};

export default CreateEditClient;
